import { useQuery } from '@tanstack/react-query';
import { decode } from 'he';
import PropTypes from 'prop-types';
import {
  useState,
  useCallback,
  forwardRef,
  useEffect,
  useRef,
  useMemo,
  useLayoutEffect,
} from 'react';
import { useLocation } from 'react-router-dom';
import styled from 'styled-components';

import { Box } from '@hivebrite/design-system/Box';
import { getAspectRatioForEmbed } from '@hivebrite/design-system/Embed';
import { Flag } from '@hivebrite/design-system/Flag';
import { Loader } from '@hivebrite/design-system/Loader';
import { useBreakpoint } from '@hivebrite/design-system/Theme';
import { toast } from '@hivebrite/design-system/Toast';
import { useEmbedLy } from '@hivebrite/react-hooks';

import { ImagePreviewModal } from 'hb-react/frontoffice/components/ImagePreviewModal';
import Embed from 'hb-react/shared/components/DesignSystem/Embed';
import { axiosInstance as axios } from 'hb-react/shared/utils/axios';
import {
  isFileAnImageOrHasAPreview,
  hasFileAPreview,
  isFileAnArticle,
  isFileAVideo,
  isFileAnAudio,
  isFileExternalSource,
  canDownloadFile,
} from 'hb-react/shared/utils/files/fileContentTypes';
import { resizeIFrameToFitContent } from 'hb-react/shared/utils/iframe';

import CategoriesList from './CategoriesList';
import NoPreviewDownload from './EmptyContent/NoPreviewDownload';
import NoPreviewExternalLink from './EmptyContent/NoPreviewExternalLink';
import NotFound from './EmptyContent/NotFound';
import VideoPlayer from './VideoPlayer';
import { incrementView, incrementDownload } from '../utils/fileAnalytics';

export const FilePreviewContent = ({ filePreview, categories = [] }) => {
  const {
    content_type: contentType,
    extension,
    file_type: fileType,
    html_ex_url: htmlExUrl,
    html_url: htmlUrl,
    is_downloadable: downloadable,
    large_url: largeUrl,
    name,
    network_category_ids: networkCategoryIds,
    url,
    download_url: downloadUrl,
    uuid,
  } = filePreview;

  useEffect(() => {
    if (uuid) {
      incrementView(uuid);
    }
  }, [uuid]);

  if (isFileExternalSource(fileType)) {
    return (
      <ExternalSourcePreview
        url={url}
        categories={categories}
        networkCategoryIds={networkCategoryIds}
      />
    );
  }

  if (isFileAVideo(fileType) || isFileAnAudio(fileType)) {
    return (
      <VideoPlayer
        url={url}
        fileType={fileType}
        categories={categories}
        networkCategoryIds={networkCategoryIds}
        downloadable={downloadable}
      />
    );
  }

  if (
    isFileAnImageOrHasAPreview(htmlUrl ?? largeUrl, contentType, extension) ||
    isFileAnArticle(fileType)
  ) {
    return (
      <ImagePreview
        categories={categories}
        contentType={contentType}
        downloadable={downloadable}
        downloadUrl={downloadUrl}
        extension={extension}
        htmlExUrl={htmlExUrl}
        htmlUrl={htmlUrl}
        largeUrl={largeUrl}
        name={name}
        networkCategoryIds={networkCategoryIds}
        url={url}
        uuid={uuid}
      />
    );
  }

  if (
    !hasFileAPreview(largeUrl, contentType) &&
    canDownloadFile({ fileType })
  ) {
    return (
      <EmptyContentWrapper>
        <NoPreviewDownload url={url} downloadable={downloadable} />
        <CategoriesList
          categories={categories}
          networkCategoryIds={networkCategoryIds}
        />
      </EmptyContentWrapper>
    );
  }

  return null;
};

FilePreviewContent.propTypes = {
  categories: PropTypes.array,
  filePreview: PropTypes.shape({
    content_type: PropTypes.string,
    extension: PropTypes.string,
    file_type: PropTypes.string,
    html_ex_url: PropTypes.string,
    html_url: PropTypes.string,
    is_downloadable: PropTypes.bool,
    large_url: PropTypes.string,
    name: PropTypes.string,
    network_category_ids: PropTypes.array,
    url: PropTypes.string,
    uuid: PropTypes.string,
  }).isRequired,
};

export default FilePreviewContent;

export const FilePreviewNoContent = () => (
  <EmptyContentWrapper>
    <NotFound />
  </EmptyContentWrapper>
);

export const ExternalSourcePreview = ({
  url,
  categories,
  networkCategoryIds,
  withDescription = false,
}) => {
  const location = useLocation();
  const breakpoint = useBreakpoint();
  const searchParams = new URLSearchParams(location.search);
  const autoplay = searchParams.has('play') || location?.state?.play;
  const { response, isLoading } = useEmbedLy(url, {
    apiUrl: `${window.location.origin}${Routes.embed_path()}`,
    autoplay,
  });

  const containerRef = useRef(null);

  const isSmallOrMediumScreen = useMemo(
    () => ['s', 'm'].includes(breakpoint),
    [breakpoint],
  );

  /**
   * Resizes the embed element to fit within the container to avoid unwanted scrolling
   * while ensuring the embed is as big as possible.
   */
  const resizeEmbedToFit = useCallback(
    (embedItem, retryCount = 0) => {
      const MAX_RETRIES = 10;

      // Get the aspect ratio of the embed
      const aspectRatio = getAspectRatioForEmbed(embedItem);

      // Validate aspect ratio to prevent division by zero or invalid values
      if (!aspectRatio || aspectRatio <= 0 || !Number.isFinite(aspectRatio)) {
        return;
      }

      const container = containerRef.current;

      if (!container) {
        return;
      }

      // Find the ds-embed element within the container
      const embed = container.querySelector('[data-testid="ds-embed"]');

      if (!embed) {
        return;
      }

      const embedVideoWrapper = embed.firstElementChild?.firstElementChild;

      if (!embedVideoWrapper) {
        return;
      }

      const embedInfoWrapper = withDescription
        ? embed.firstElementChild?.lastElementChild
        : null;

      const categoriesList = container.querySelector(
        '[data-testid="categories-list"]',
      );

      // Make sure the embed overflow is visible so we get the correct dimensions
      embed.style.overflow = 'visible';

      // Temporarily set container height and width to 100% to get the correct dimensions
      container.style.height = '100%';
      container.style.width = '100%';

      try {
        // Get container dimensions
        const containerRect = container.getBoundingClientRect();
        const containerWidth = containerRect.width;
        const containerHeight = containerRect.height;

        // If container width is 0, the layout hasn't settled yet
        // Retry with progressive delay to allow time for layout to settle
        if (containerWidth === 0 && retryCount < MAX_RETRIES) {
          container.style.height = 'auto';

          // Progressive delays
          const delay = retryCount * 10;

          const retry = () => {
            requestAnimationFrame(() => {
              resizeEmbedToFit(embedItem, retryCount + 1);
            });
          };

          setTimeout(retry, delay);

          return;
        }

        // If we've exceeded max retries, log a warning and give up
        if (containerWidth === 0 && retryCount >= MAX_RETRIES) {
          console.warn(
            'Failed to resize embed: container width is 0 after maximum retries',
          );
          container.style.height = 'auto';
          return;
        }

        const categoriesListHeight = categoriesList
          ? categoriesList.getBoundingClientRect().height
          : 0;

        const embedInfoWrapperHeight = embedInfoWrapper
          ? embedInfoWrapper.getBoundingClientRect().height
          : 0;

        // This is the height the video would have if it was full width
        const fullSizeVideoHeight = containerWidth / aspectRatio;

        // This is the maximum height the video can be without overflowing the container
        let maxVideoHeight =
          containerHeight - categoriesListHeight - embedInfoWrapperHeight;

        // When on Small or Medium screen, we use the Mobile view of the embed
        // and we must use the modal height as the max height
        if (isSmallOrMediumScreen) {
          const modal = document.querySelector(
            '[data-testid="file-preview-modal-container"]',
          );

          if (modal) {
            const modalComputedStyle = window.getComputedStyle(modal);
            const modalClientHeight = modal.clientHeight;
            const paddingTop = parseFloat(modalComputedStyle.paddingTop);
            const paddingBottom = parseFloat(modalComputedStyle.paddingBottom);
            const modalHeight = modalClientHeight - paddingTop - paddingBottom;

            const mobileSwipper = document.querySelector(
              '[data-testid="mobile-swipper"]',
            );

            const mobileSwipperHeaderHeight =
              mobileSwipper?.firstElementChild?.clientHeight ?? 0;

            maxVideoHeight = modalHeight - mobileSwipperHeaderHeight;
          }
        }

        const newHeight = Math.min(maxVideoHeight, fullSizeVideoHeight);

        // Apply the computed styles
        embedVideoWrapper.style.height = `${newHeight}px`;
        embedVideoWrapper.style.width = `${containerWidth}px`;
        embedVideoWrapper.style.aspectRatio = 'auto';
      } catch (error) {
        console.warn('Failed to resize embed:', error);
      } finally {
        // Always reset container height to auto, even if an error occurred
        container.style.height = 'auto';
      }
    },
    [isSmallOrMediumScreen, withDescription],
  );

  // Resize embed when response changes or component mounts
  useLayoutEffect(() => {
    if (response && !isLoading) {
      resizeEmbedToFit(response);
    }
  }, [response, isLoading, resizeEmbedToFit]);

  // Resize on window resize
  useLayoutEffect(() => {
    const handleResize = () => {
      if (response && !isLoading) {
        resizeEmbedToFit(response);
      }
    };

    window.addEventListener('resize', handleResize);

    return () => window.removeEventListener('resize', handleResize);
  }, [response, isLoading, resizeEmbedToFit]);

  return (
    <OtherContentWrapper ref={containerRef} data-testid="external-preview">
      {response && (
        <Embed
          withDescription={withDescription}
          embed={{
            ...response,
            height:
              response && breakpoint !== 's' && response.height < 900
                ? response.height
                : null,
            provider_url: !response ? url : response.provider_url,
            url,
          }}
          isLoading={isLoading}
          autoplay={autoplay}
          renderEmptyPreview={() => <NoPreviewExternalLink />}
        />
      )}
      <CategoriesList
        categories={categories}
        networkCategoryIds={networkCategoryIds}
      />
    </OtherContentWrapper>
  );
};

ExternalSourcePreview.propTypes = {
  categories: PropTypes.array,
  withDescription: PropTypes.bool,
  networkCategoryIds: PropTypes.array,
  url: PropTypes.string.isRequired,
};

const ImagePreview = ({
  largeUrl,
  htmlUrl,
  htmlExUrl,
  extension,
  contentType,
  categories,
  networkCategoryIds,
  name,
  uuid,
  ...rest
}) => {
  const [isImageLoaded, setIsImageLoaded] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const breakpoint = useBreakpoint();

  const imageRef = useCallback((node) => {
    if (node !== null) {
      node.onload = () => setIsImageLoaded(true);
    } else {
      setIsImageLoaded(false);
    }
  }, []);

  const isArticle = extension === 'article';
  const isPdf = extension === 'pdf' || contentType.includes('pdf');

  const pdfPreviewUrl = htmlExUrl || htmlUrl;

  const { isLoading: pdfPreviewLoading, data: pdfPreviewHtml } = useQuery({
    queryKey: ['pdf-preview', pdfPreviewUrl],
    queryFn: () =>
      axios
        .get(pdfPreviewUrl, {
          withCredentials: false,
          headers: {
            Accept: 'text/html; charset=utf-8',
          },
          responseType: 'document',
        })
        .then(
          ({ data }) => data && new XMLSerializer().serializeToString(data),
        ),
  });

  useEffect(() => {
    if (isPdf && pdfPreviewUrl && pdfPreviewHtml) {
      toast(__('This is a generated preview and might not be 100% accurate.'));
    }
  }, [pdfPreviewUrl, isPdf, pdfPreviewHtml]);

  if (isPdf) {
    if (pdfPreviewUrl) {
      if (pdfPreviewLoading) {
        return <Loader size={50} />;
      }

      return (
        <Box
          overflow="scroll"
          flex="1 1 100%"
          alignSelf="stretch"
          display="block"
        >
          <iframe
            data-testid="html-pdf-preview"
            src={pdfPreviewUrl}
            srcDoc={pdfPreviewHtml}
            alt={__('Preview of the PDF')}
            width="100%"
            height="100%"
            onLoad={({ target }) =>
              pdfPreviewHtml && resizeIFrameToFitContent({ target, breakpoint })
            }
            scrolling="no"
          />
        </Box>
      );
    }

    return (
      <ImageWrapper>
        <PDFPreview
          largeUrl={largeUrl}
          ref={imageRef}
          isImageLoaded={isImageLoaded}
          uuid={uuid}
          {...rest}
        />
        {isImageLoaded && (
          <CategoriesList
            categories={categories}
            networkCategoryIds={networkCategoryIds}
          />
        )}
      </ImageWrapper>
    );
  }

  return (
    <ImageWrapper>
      <Container>
        <ImageStyled
          data-testid="image-preview"
          src={largeUrl}
          key={largeUrl}
          alt=""
          ref={imageRef}
          onClick={() => setIsOpen(true)}
        />
        <ImagePreviewModal
          handleCloseModal={() => setIsOpen(false)}
          images={[{ default_url: largeUrl }]}
          isOpen={isOpen}
        />
        {isImageLoaded && isArticle && (
          <Flag
            text={decode(name)}
            position="absolute"
            bottom={2}
            left={2}
            maxWidth="90%"
          />
        )}
      </Container>
      {isImageLoaded && (
        <CategoriesList
          categories={categories}
          networkCategoryIds={networkCategoryIds}
        />
      )}
    </ImageWrapper>
  );
};

ImagePreview.propTypes = {
  categories: PropTypes.array,
  contentType: PropTypes.string,
  extension: PropTypes.string,
  htmlExUrl: PropTypes.string,
  htmlUrl: PropTypes.string,
  largeUrl: PropTypes.string,
  name: PropTypes.string,
  networkCategoryIds: PropTypes.array,
  uuid: PropTypes.string,
};

export const PDFPreview = forwardRef(
  (
    { downloadUrl, largeUrl, isImageLoaded = false, downloadable, uuid },
    ref,
  ) => (
    <Container>
      <ImageStyled
        data-testid="image-pdf-preview"
        src={largeUrl}
        key={largeUrl}
        alt={__('Preview of the PDF')}
        ref={ref}
      />

      {isImageLoaded && (
        <Overlay data-testid="overlay-pdf-preview">
          {downloadable ? (
            <Paragraph
              onClick={() => {
                if (uuid) {
                  incrementDownload(uuid);
                }
              }}
              dangerouslySetInnerHTML={{
                __html: __(
                  `This is just a preview of the first page of this PDF. To view it entirely, please <a href="%{downloadUrl}" target="_blank">download</a> the file.`,
                ).i__({ downloadUrl }),
              }}
            />
          ) : (
            <Paragraph
              dangerouslySetInnerHTML={{
                __html: __(
                  'This is just a preview of the first page of this PDF. To view it entirely, please contact your admin.',
                ),
              }}
            />
          )}
        </Overlay>
      )}
    </Container>
  ),
);

PDFPreview.propTypes = {
  isImageLoaded: PropTypes.bool,
  largeUrl: PropTypes.string.isRequired,
  downloadUrl: PropTypes.string.isRequired,
};

const Container = styled.div`
  position: relative;
  min-height: 0;
  min-width: 0;
  display: flex;
  justify-content: center;
  height: 100%;
`;

const Overlay = styled(Box)`
  background-color: rgba(0, 0, 0, 0.5);
  height: 100%;
  position: absolute;
  text-align: center;
  width: 100%;
  top: 0;
`;

const Paragraph = styled.p`
  background-color: white;
  margin: auto;
  padding: 12px 20px;
  width: 80%;

  a {
    color: ${({ theme }) => theme.colors.link};
    text-decoration: underline;
  }
`;

const ImageStyled = styled.img`
  cursor: pointer;
  max-height: 100%;
  min-height: 0;
  min-width: 0;
  margin: auto;
  object-fit: contain;
`;

const ImageWrapper = ({ children }) => (
  <Box maxHeight="100%" height="100%" justifyContent="center">
    <Box flexDirection="column" alignItems="center">
      {children}
    </Box>
  </Box>
);

const OtherContentWrapper = styled(Box).attrs({
  display: 'flex',
  width: '100%',
  maxWidth: '100%',
  maxHeight: '100%',
  alignItems: 'center',
  margin: 'auto',
  flexDirection: 'column',
  justifyContent: 'center',
})``;

const EmptyContentWrapper = styled(Box).attrs({
  display: 'flex',
  width: { _: '100%', l: '72%' },
  height: { _: '100%', l: '90%' },
  alignItems: 'center',
  margin: 'auto',
  flexDirection: 'column',
  justifyContent: 'center',
})``;
